import RichText from '@/components/RichText';
import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { IHowTrackingWorks, IRichText } from '@/interfaces';
import { GridSectionTitle, InsightsContainer } from '../TrackingInsights.styles';
import { interpolate } from '../interpolate.utils';
import { Body, Section } from './HowTrackingWorks.styles';

// The field is a bare rich-text document: no embedded assets or entries, so nothing to link.
const NO_LINKS: IRichText['links'] = { assets: { block: [] }, entries: { block: [] } };

export interface HowTrackingWorksProps {
  content: IHowTrackingWorks;
  /** Fills the `{carrier}` placeholder of the default heading. */
  carrierName?: string;
}

/**
 * Long-form explainer for one carrier's tracking.
 *
 * Growth writes this copy as Heading 3 blocks (tracking number, tracking status, supported
 * services, plus carrier-specific extras such as customs or pickup deadlines) with paragraphs and
 * bullet lists underneath, so it is stored and rendered as one rich-text document — the same
 * shape as the FAQ block — rather than as a fixed set of three fields. The prose runs at the
 * standard 1120px content width, narrower than the 1160px card grids above it.
 */
const HowTrackingWorks: React.FC<HowTrackingWorksProps> = ({ content, carrierName }) => {
  const { t } = useGlobalState();

  if (!content.content?.content?.length) return null;

  // Without a carrier name (a page with this block but no carrier information) the heading drops
  // the name rather than printing "How  Tracking Works".
  const title =
    content.title ||
    (carrierName && interpolate(t(MasterTranslation.HowTrackingWorksTitle), 'carrier', carrierName)) ||
    t(MasterTranslation.HowTrackingWorksTitleGeneric);

  return (
    <Section>
      <InsightsContainer>
        <GridSectionTitle type='h2'>{title}</GridSectionTitle>
        <Body>
          <RichText richText={{ json: content.content, links: NO_LINKS }} />
        </Body>
      </InsightsContainer>
    </Section>
  );
};

export default HowTrackingWorks;
